import Header from './components/Header';
import Body from './components/Body';
import Link from 'next/link';
import { styles } from './styles';
import classNames from 'classnames';
import Section, { SectionType } from './components/Section';

const items: SectionType[] = [
  {
    title: 'What does this policy cover?',
    content: (
      <>
        Welcome to MindFeed, the online and mobile service of MindFeed Inc.
        (“MindFeed,” “we,” or “us”). Our Privacy Policy explains how MindFeed
        and its affiliates collect, use, disclose, and protect information that
        applies to our memory organization platform (the &#34;Service&#34;), and
        your choices regarding the collection and use of your information.
        Capitalized terms that are not defined in this Privacy Policy have the
        meaning given to them in our{' '}
        <Link className="text-mf-blue-base" href="/terms-of-use">
          Terms of Use
        </Link>
        . If you do not want your information processed in accordance with this
        Privacy Policy in general or any part of it, you should not use our
        Service. This policy applies to all users of the Service.
      </>
    ),
    summary: (
      <>
        Summary: Welcome, here is our policy on privacy. This policy sets out
        how MindFeed collects and uses the information that we collect about you
        when you use the Service. This policy also explains the choices that you
        can make about the way that we use your information.
      </>
    ),
  },
  {
    title: '1. Information we collect',
    content: (
      <>
        When you use MindFeed, we collect different types of information to
        ensure the proper functioning of our services and to enhance your user
        experience. When you create an account, we collect basic personal
        details such as your name and email address. This allows us to identify
        you and provide you with secure access to our platform. Additionally, we
        collect content you add to MindFeed, such as memos and collections, to
        enable you to organize and manage your information effectively. We also
        gather technical data, including your IP address, device type, and usage
        logs. This helps us optimize performance, troubleshoot issues, and
        improve the overall stability and security of our platform.
      </>
    ),
    summary: (
      <>
        Summary: We collect info about you that you choose to give us, for
        example when you register an account, use the Service or otherwise
        interact with us.
      </>
    ),
  },
  {
    title: '2. Sharing your information',
    content: (
      <>
        We use the information we collect to operate, maintain, and improve
        MindFeed. Your personal data helps us ensure that you have a seamless
        and personalized experience on our platform. By analyzing how you
        interact with the Service, we can enhance its functionality, optimize
        performance, and introduce new features that align with user needs. We
        also use your data to communicate with you, whether to send important
        updates about our Service, respond to support requests, or provide
        personalized recommendations. These communications may include security
        alerts, technical notices, or feature updates to improve your
        experience.
        <br />
        <br />
        Security is a key priority, and we use collected data to detect,
        prevent, and respond to fraudulent activities or unauthorized access
        attempts. By monitoring usage patterns and applying security protocols,
        we work to keep your data safe and ensure compliance with applicable
        legal and regulatory requirements.
      </>
    ),
    summary: (
      <>
        Summary: MindFeed uses information about you for different reasons,
        including to provide, customize and improve the Service.
      </>
    ),
  },
  {
    title: '3. Data Sharing',
    content: (
      <>
        We are committed to protecting the confidentiality of your personal
        data. However, there may be situations where it is necessary to share
        your information with third parties. Here are the cases in which we may
        share your data:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li>
            <strong>For the proper functioning of the service:</strong> To
            ensure the quality and continuity of our service, we may need to
            share certain information with technical service providers, hosting
            companies, or subcontracting partners who help us manage the app’s
            infrastructure and improve the user experience. These parties can
            only use your data to the extent necessary for the provision of
            their services and are required to maintain the confidentiality of
            the data.
          </li>
          <li>
            <strong>
              To comply with legal obligations or respond to government
              requests:
            </strong>
            In certain circumstances, we may be required to share your
            information with public authorities or government bodies, if
            required by law, a legal procedure, or to comply with applicable
            regulations.
          </li>
          <li>
            <strong>With your explicit consent:</strong> When you give us your
            explicit consent, we may share your personal data with specific
            third parties for particular reasons, such as sending you offers or
            relevant communications.
          </li>
        </ul>
        <br />
        <strong>Sharing Memos via a Public Link</strong>
        <br />
        It is important to note that if you choose to share a memo via a public
        link, it may be accessible to anyone who has the link. While we do our
        best to protect your privacy, we cannot guarantee the confidentiality of
        memos shared publicly.
        <br />
        <br />
        Therefore, we advise you not to include sensitive information in memos
        that you share publicly, such as:
        <ul className="list-disc pl-5">
          <li>Passwords or login credentials</li>
          <li>Banking details or credit card information</li>
          <li>
            Sensitive personal information (e.g., social security number, full
            address)
          </li>
          <li>Medical details or health information</li>
        </ul>
        <br />
        Please exercise caution when choosing to share a memo in this way
      </>
    ),
    summary: (
      <>
        Summary: We may share your data with third parties for service
        functionality, legal compliance, or with your consent, and advise
        caution when sharing sensitive information via public links.
      </>
    ),
  },
  {
    title: '4. How we transfer, store and protect your data',
    content: (
      <>
        Your information collected through the Service will be stored and
        processed in Australia, the United States, France, and any other country
        where MindFeed or its subsidiaries, affiliates, or service providers
        operate or employ staff or contractors. We may transfer the information
        we collect about you, including personal information, to affiliated
        entities and other third parties across borders, from your country or
        jurisdiction to other countries or jurisdictions around the world.
        <br />
        <br />
        As a result, your data, including personal information, may be
        transferred to countries or jurisdictions that may not offer the same
        level of data protection as your own. However, we take steps to ensure
        that your information remains protected wherever it is stored and
        processed, in accordance with applicable laws.
        <br />
        By using the Service, you consent to the transfer of your information to
        the United States, France, Australia, or any other country where
        MindFeed or its parent company, subsidiaries, affiliates, or service
        providers maintain facilities, and to the use and disclosure of your
        information as described in this Privacy Policy.
      </>
    ),
    summary: (
      <>
        Your information may be stored and processed across multiple countries,
        including Australia, the US, and France, and may be transferred to
        jurisdictions with different data protection laws.
      </>
    ),
  },
  {
    title: '5. Keeping your information safe',
    content: (
      <>
        Your information collected through the Service will be stored and
        processed in Australia, the United States, France, and any other country
        where MindFeed or its subsidiaries, affiliates, or service providers
        operate or employ staff or contractors. We may transfer the information
        we collect about you, including personal information, to affiliated
        entities and other third parties across borders, from your country or
        jurisdiction to other countries or jurisdictions around the world.
        <br />
        <br />
        As a result, your data, including personal information, may be
        transferred to countries or jurisdictions that may not offer the same
        level of data protection as your own. However, we take steps to ensure
        that your information remains protected wherever it is stored and
        processed, in accordance with applicable laws.
        <br />
        <br />
        By using the Service, you consent to the transfer of your information to
        the United States, France, Australia, or any other country where
        MindFeed or its parent company, subsidiaries, affiliates, or service
        providers maintain facilities, and to the use and disclosure of your
        information as described in this Privacy Policy.
      </>
    ),
    summary: (
      <>
        MindFeed uses Google, Facebook, and Apple for authentication and takes
        steps to protect your data, but cannot guarantee its security,
        especially regarding third-party services.
      </>
    ),
  },
  {
    title: '6. How long we keep your information',
    content: (
      <>
        Following the termination or deactivation of your user account, MindFeed
        will retain your profile information and User Content for a commercially
        reasonable period, and as long as we have a valid purpose to do so.
        Specifically, MindFeed will retain your information to comply with legal
        obligations, audit requirements, and for backup and archival purposes.
      </>
    ),
    summary: (
      <>
        MindFeed will retain your profile information and User Content for a
        reasonable period after account termination, to comply with legal
        obligations and for backup purposes.
      </>
    ),
  },
  {
    title: '7. Links to other websites and services',
    content: (
      <>
        Following the termination or deactivation of your user account, MindFeed
        will retain your profile information and User Content for a commercially
        reasonable period, and as long as we have a valid purpose to do so.
        Specifically, MindFeed will retain your information to comply with legal
        obligations, audit requirements, and for backup and archival purposes.
      </>
    ),
    summary: (
      <>
        MindFeed is not responsible for third-party websites or services linked
        from the app, and interactions with them are subject to their own
        policies.
      </>
    ),
  },
  {
    title: '8. Additional information for users in Europe',
    content: (
      <>
        This section of the policy applies to MindFeed users located in the
        European Economic Area (EEA), Switzerland, or the United Kingdom (UK).
        MindFeed processes your personal data in accordance with European laws
        and regulations, including the General Data Protection Regulation (GDPR)
        and UK General Data Protection Regulation (UK GDPR).
        <br />
        <br />
        <strong>(a) Controller&#39;s Details</strong>
        <br />
        <br />
        For the purposes of the GDPR and UK GDPR, MindFeed Pty Ltd is the
        controller of your personal data. MindFeed Pty Ltd’s registered address
        is at 17 Cliff Street, Manly, NSW, 2095, Australia and we can be
        contacted using the details provided in section 11 below “How to contact
        us” heading.
        <br />
        <br />
        In certain circumstances, we may process personal data as a processor on
        behalf of our customer. MindFeed’s Terms of Use outline the
        circumstances in which we process personal data as a processor. In such
        cases, the customer is responsible for the processing of your personal
        data, and our processing will be governed by the terms of the Data
        Processing Addendum entered into with the customer. If you have
        questions about how your personal data is handled by a customer, you
        should contact the relevant customer and refer to their separate privacy
        policies.
        <br />
        <br />
        <strong>(b) Legal Bases for Processing</strong>
        <br />
        <br />
        If you are located in the EEA, Switzerland, or UK, we need a lawful
        basis to collect, use, and disclose your personal data as a controller.
        The lawful basis will depend on the information concerned and the
        context in which it is processed.
        <br />
        <br />
        Generally, MindFeed will collect and use your information as follows:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li>
            <strong>Contractual necessity: </strong> We need your information to
            provide the Service and fulfill our obligations to you under our
            Terms of Use. This includes account creation, resolving issues,
            obtaining payments, and providing access to your data.
          </li>
          <li>
            <strong>Legitimate interests: </strong> It is necessary for our
            legitimate interests, for example, to improve our Service, provide
            relevant marketing messages, display ads, track effectiveness,
            analyze usage data, and enhance the Service through research and
            development.
          </li>
          <li>
            <strong>Consent: </strong> You consent to us, and our third-party
            partners, using your information for certain purposes, such as
            marketing or machine learning related to your User Content.
          </li>
          <li>
            <strong>Legal obligations: </strong> It is necessary to comply with
            legal obligations, such as responding to law enforcement requests or
            retaining data for record-keeping.
          </li>
        </ul>
        <br />
        If you have consented to the use of your information, you can withdraw
        that consent at any time. You can withdraw your consent by emailing
        [insert email] or adjusting your privacy settings.
        <br />
        If we use your information for legitimate interests, you have the right
        to object. However, this may affect your experience with the Service.
        <br />
        <br />
        <strong>(c) Cross-border Transfers of Information</strong>
        <br />
        <br />
        By signing up, you acknowledge and agree that MindFeed Pty Ltd (based in
        Australia) will receive and process your personal data. We may transfer
        your data to other countries where our affiliates and service providers
        are located. Some of these countries may have data protection laws that
        are different from your country (and may not offer the same level of
        protection).
        <br />
        <br />
        For users in the EEA, Switzerland, or UK, if we transfer your personal
        data to third-party service providers outside of Europe, and those
        countries do not have an adequacy decision from the European Commission,
        UK, or Swiss authorities, we will require those third parties to enter
        into an agreement that provides appropriate safeguards for your data.
        These safeguards include the use of EU Model Clauses and the UK
        International Data Transfer Addendum.
      </>
    ),
    summary: (
      <>
        MindFeed processes personal data in compliance with GDPR and UK GDPR for
        users in the EEA, UK, and Switzerland, with cross-border data transfers
        subject to appropriate safeguards.
      </>
    ),
  },
  {
    title: '9. Additional information for users in the United States',
    content: (
      <>
        This section of the policy applies to MindFeed users located in the
        United States. MindFeed processes your personal data in accordance with
        applicable U.S. laws and regulations, including relevant privacy laws
        such as the California Consumer Privacy Act (CCPA), if applicable.{' '}
        <br />
        <br />
        <strong>(a) Controller&#39;s Details</strong>
        <br />
        <br />
        For the purposes of U.S. data protection laws, MindFeed Pty Ltd is the
        controller of your personal data. MindFeed Pty Ltd’s registered address
        is at 17 Cliff Street, Manly, NSW, 2095, Australia, and we can be
        contacted using the details provided in section 11 below “How to contact
        us” heading. <br />
        <br />
        In certain circumstances, we may process personal data as a processor on
        behalf of our customer. MindFeed’s Terms of Use outline the
        circumstances in which we process personal data as a processor. In such
        cases, the customer is responsible for the processing of your personal
        data, and our processing will be governed by the terms of the Data
        Processing Addendum entered into with the customer. If you have
        questions about how your personal data is handled by a customer, you
        should contact the relevant customer and refer to their separate privacy
        policies. <br />
        <br />
        <strong>(b) Legal Bases for Processing</strong>
        <br />
        <br />
        If you are located in the United States, we process your personal data
        for several lawful reasons, including: <br />
        <br />
        <ul className="list-disc pl-5">
          <li>
            Contractual necessity: We need your information to provide the
            Service and fulfill our obligations to you under our Terms of Use.
            This includes account creation, resolving issues, obtaining
            payments, and providing access to your data.{' '}
          </li>
          <li>
            Legitimate interests: We may use your information for legitimate
            interests such as improving our Service, sending marketing messages,
            displaying ads, tracking effectiveness, analyzing usage data, and
            enhancing the Service through research and development.{' '}
          </li>
          <li>
            Consent: You consent to us, and our third-party partners, using your
            information for specific purposes, such as marketing or machine
            learning related to your User Content.{' '}
          </li>
          <li>
            Legal obligations: We may need to process your information to comply
            with legal obligations, such as responding to law enforcement
            requests or retaining data for record-keeping purposes.{' '}
          </li>
        </ul>
        <br />
        If you have consented to the use of your information, you can withdraw
        that consent at any time. You can withdraw your consent by emailing
        [insert email] or adjusting your privacy settings.
        <br />
        <br />
        <strong>(c) Cross-border Transfers of Information</strong>
        <br />
        <br />
        By signing up, you acknowledge and agree that MindFeed Pty Ltd (based in
        Australia) will receive and process your personal data. We may transfer
        your data to other countries where our affiliates and service providers
        are located. Some of these countries may have data protection laws that
        are different from those in the United States (and may not offer the
        same level of protection). <br />
        <br />
        For users in the United States, if we transfer your personal data to
        third-party service providers outside of the U.S., we will ensure that
        appropriate safeguards are in place to protect your data in accordance
        with applicable laws, including using standard contractual clauses, as
        required.{' '}
      </>
    ),
    summary: (
      <>
        Summary: MindFeed processes user data in compliance with U.S. laws and
        ensures appropriate safeguards for cross-border data transfers.{' '}
      </>
    ),
  },
  {
    title: '10. Changes to this Policy',
    content: (
      <>
        We may update this policy periodically to reflect our current practices
        and ensure compliance with applicable laws. When we make changes to this
        policy, we will update the &#34;Last Updated&#34; date at the top of
        this page. If we make significant changes to how we collect, use, store,
        or share your personal information, we will notify you by sending an
        email to the address associated with your MindFeed account. We recommend
        checking this page regularly to stay informed of any updates.{' '}
      </>
    ),
    summary: (
      <>
        We won’t make any major changes to our Privacy Policy without giving
        notice – but it’s still a good idea to visit this page every now and
        then.{' '}
      </>
    ),
  },
  {
    title: '11. How to contact us',
    content: (
      <>
        If you have any questions about this Privacy Policy or the Service, or
        wish to make a complaint please contact us at:
        <br />
        <br />
        Email:{' '}
        <Link
          className="text-mf-blue-base"
          href={`mailto:support@mindfeed.app`}
        >
          support@mindfeed.app
        </Link>
      </>
    ),
    summary: (
      <>
        We care about your privacy and are more than happy to respond to any of
        your questions.
      </>
    ),
  },
];

const PrivacyPolicy = (): JSX.Element => (
  <div className="p-0 pt-16 flex flex-col font-inter text-mf-off-black-950">
    <Header title="Privacy Policy" />
    <Body>
      <p className={classNames(styles.text, 'mb-9')}>
        This Privacy Policy was last updated on January 01, 2025.
      </p>
      {items.map((item, i) => (
        <Section {...item} key={i} />
      ))}
    </Body>
  </div>
);

export default PrivacyPolicy;
